import React from 'react'
import { Link } from 'react-router-dom'
import {Clock, MapPin, ExternalLink, Star, CreditCard, Users, AlertCircle} from 'lucide-react'

function HedgehogCafePage() {
  const topPicks = [
    {
      id: 1,
      type: 'Ticket',
      name: 'Hedgehog Cafe Harry Entry',
      price: '¥1,500',
      rating: 4.7,
      link: '#'
    },
    {
      id: 2,
      type: 'Tour',
      name: 'Weird Tokyo Tour Package',
      price: '¥8,500',
      rating: 4.9,
      link: '#'
    },
    {
      id: 3,
      type: 'Hotel',
      name: 'Hotel Gracery Shinjuku',
      price: '¥15,000/night',
      rating: 4.6,
      link: '#'
    }
  ]

  return (
    <div className="pt-16 min-h-screen bg-[#0A0A0A]">
      {/* Hero Image */}
      <div className="relative h-[60vh] overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1618945524163-32451704651e?w=1200&q=80"
          alt="Cute hedgehog"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/50 to-transparent" />
        
        {/* Breadcrumb */}
        <div className="absolute top-8 left-0 right-0">
          <div className="max-w-5xl mx-auto px-4">
            <Link to="/" className="text-gray-400 hover:text-[#FF006E] transition-colors">
              Home
            </Link>
            <span className="text-gray-600 mx-2">/</span>
            <Link to="/weird-tokyo" className="text-gray-400 hover:text-[#FF006E] transition-colors">
              Weird Tokyo
            </Link>
          </div>
        </div>

        {/* Title */}
        <div className="absolute bottom-12 left-0 right-0">
          <div className="max-w-5xl mx-auto px-4">
            <span className="inline-block px-4 py-1 bg-[#FF006E] text-white font-bold text-sm rounded-full mb-4">
              Weird Tokyo
            </span>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Hedgehog Cafes: Tokyo's Cutest (and Spikiest) Experience
            </h1>
            <div className="flex items-center gap-4 text-gray-400 text-sm">
              <span className="flex items-center gap-1">
                <Clock className="w-4 h-4" />
                6 min read
              </span>
              <span>•</span>
              <span>Updated January 2024</span>
            </div>
          </div>
        </div>
      </div>

      {/* Content Layout */}
      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Main Content */}
          <article className="lg:w-2/3">
            {/* Quick Summary Box */}
            <div className="bg-gradient-to-br from-[#FF006E]/10 to-[#00E5FF]/10 border-2 border-[#FF006E]/30 rounded-2xl p-8 mb-8">
              <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
                <span className="text-[#FF006E]">🦔</span>
                Quick Summary
              </h2>
              <p className="text-gray-300 leading-relaxed mb-6">
                Hedgehog cafes are exactly what they sound like: cafes where you can hold, pet, and play with adorable hedgehogs. 
                It's one of Tokyo's most unique animal cafe experiences, and yes, it's as cute as it sounds. 
                This guide covers the best hedgehog cafes in Tokyo and what to expect.
              </p>

              {/* Quick Info Grid */}
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div className="bg-[#0A0A0A]/50 p-4 rounded-xl">
                  <div className="flex items-center gap-2 text-gray-400 text-sm mb-1">
                    <Clock className="w-4 h-4" />
                    Duration
                  </div>
                  <div className="text-white font-semibold">1 hour session</div>
                </div>
                <div className="bg-[#0A0A0A]/50 p-4 rounded-xl">
                  <div className="flex items-center gap-2 text-gray-400 text-sm mb-1">
                    <CreditCard className="w-4 h-4" />
                    Entry Fee
                  </div>
                  <div className="text-white font-semibold">¥1,500-2,000</div>
                </div>
                <div className="bg-[#0A0A0A]/50 p-4 rounded-xl">
                  <div className="flex items-center gap-2 text-gray-400 text-sm mb-1">
                    <MapPin className="w-4 h-4" />
                    Location
                  </div>
                  <div className="text-white font-semibold">Roppongi & Harajuku</div>
                </div>
                <div className="bg-[#0A0A0A]/50 p-4 rounded-xl">
                  <div className="flex items-center gap-2 text-gray-400 text-sm mb-1">
                    <Users className="w-4 h-4" />
                    Age Limit
                  </div>
                  <div className="text-white font-semibold">6+ years old</div>
                </div>
              </div>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-3">
                <a href="https://www.klook.com/en-US/search/?query=tokyo+animal+cafe" target="_blank" rel="noopener noreferrer" className="flex-1 px-6 py-3 bg-gradient-to-r from-[#FF006E] to-[#CC0058] text-white font-bold rounded-full hover:shadow-lg hover:shadow-[#FF006E]/50 transition-all flex items-center justify-center gap-2">
                  <ExternalLink className="w-4 h-4" />
                  Check Ticket Availability
                </a>
                <a href="https://www.google.com/maps/search/hedgehog+cafe+tokyo" target="_blank" rel="noopener noreferrer" className="flex-1 px-6 py-3 bg-gradient-to-r from-[#00E5FF] to-[#00B8CC] text-white font-bold rounded-full hover:shadow-lg hover:shadow-[#00E5FF]/50 transition-all flex items-center justify-center gap-2">
                  <MapPin className="w-4 h-4" />
                  Find Locations
                </a>
              </div>
            </div>

            {/* Article Content */}
            <div className="prose prose-invert prose-lg max-w-none">
              <div className="text-gray-300 space-y-6">
                <p className="text-xl leading-relaxed">
                  In a city known for its cat cafes, owl cafes, and even snake cafes, hedgehog cafes stand out as one of the 
                  most uniquely Tokyo experiences you can have. These tiny, adorable creatures are surprisingly friendly 
                  (once you learn how to hold them properly), and spending time with them is weirdly therapeutic.
                </p>

                <h2 className="text-3xl font-bold text-white mt-12 mb-4">Best Hedgehog Cafes in Tokyo</h2>
                
                <h3 className="text-2xl font-bold text-[#FF006E] mt-8 mb-3">1. Harry Hedgehog Cafe (Roppongi)</h3>
                <p>
                  The most popular hedgehog cafe in Tokyo. Located in Roppongi, Harry has around 30 hedgehogs with different 
                  colors and personalities. Entry is ¥1,500 for a 1-hour session, which includes a drink. Reservations are 
                  <strong> highly recommended</strong>—they book out weeks in advance on weekends.
                </p>
                <div className="bg-[#1A1A1A] rounded-xl p-6 my-6">
                  <p className="text-gray-300 mb-0">
                    <strong>Address:</strong> 3-15-10 Roppongi, Minato-ku<br/>
                    <strong>Hours:</strong> 12:00 PM - 10:00 PM (Last entry 9:00 PM)<br/>
                    <strong>Price:</strong> ¥1,500 (1 hour) + 1 drink included
                  </p>
                </div>

                <h3 className="text-2xl font-bold text-[#FF006E] mt-8 mb-3">2. Chiku Chiku Cafe (Harajuku)</h3>
                <p>
                  A smaller, cozier option near Harajuku. Chiku Chiku has about 15 hedgehogs and a more relaxed vibe. 
                  Entry is ¥2,000 for 90 minutes, but you get more one-on-one time with the hedgehogs. 
                  Great for photos and a quieter experience.
                </p>

                <div className="bg-[#FF006E]/10 border-l-4 border-[#FF006E] p-6 rounded-r-lg my-8">
                  <h4 className="text-xl font-bold text-white mb-2 flex items-center gap-2">
                    <AlertCircle className="w-5 h-5 text-[#FF006E]" />
                    Important Rules
                  </h4>
                  <ul className="text-gray-300 space-y-2 list-none pl-0">
                    <li>• Wash your hands before and after handling hedgehogs</li>
                    <li>• Don't touch their faces or bellies</li>
                    <li>• Let them come to you—don't chase them</li>
                    <li>• No flash photography (it scares them)</li>
                    <li>• Kids under 6 are not allowed for safety reasons</li>
                  </ul>
                </div>

                <h2 className="text-3xl font-bold text-white mt-12 mb-4">What to Expect</h2>
                <p>
                  When you arrive, staff will explain how to properly hold a hedgehog (use both hands, support their belly, 
                  let them curl into a ball if they want). You'll get gloves to start, but once the hedgehogs relax, 
                  you can usually hold them barehanded.
                </p>
                <p>
                  Most cafes have a "hedgehog menu" where you can choose which one you want to interact with based on 
                  their color and temperament. Some are super friendly, others are sleepy (hedgehogs are nocturnal, 
                  so daytime visits mean sleepier hedgies).
                </p>

                <h3 className="text-2xl font-bold text-[#FF006E] mt-8 mb-3">Photography Tips</h3>
                <ul className="space-y-2 text-gray-300 list-disc pl-6">
                  <li>Use natural light—no flash</li>
                  <li>Get down to hedgehog eye level for better shots</li>
                  <li>Capture the moment they uncurl (super cute!)</li>
                  <li>Use burst mode for action shots</li>
                </ul>

                <div className="bg-[#1A1A1A] border-l-4 border-[#00E5FF] p-6 rounded-r-lg my-8">
                  <h4 className="text-xl font-bold text-white mb-2">💡 Insider Tip</h4>
                  <p className="text-gray-300 mb-0">
                    Book an evening session (after 6 PM) if possible. Hedgehogs are nocturnal, so they're more active 
                    and playful in the evening. Morning sessions often have sleepy hedgehogs that just want to nap.
                  </p>
                </div>

                <h2 className="text-3xl font-bold text-white mt-12 mb-4">Combining with Other Weird Tokyo Spots</h2>
                <p>
                  Make it a "weird Tokyo" day by combining your hedgehog cafe visit with other unique attractions nearby:
                </p>
                <ul className="space-y-2 text-gray-300 list-disc pl-6">
                  <li><strong>From Roppongi:</strong> Visit the Mori Art Museum, then hit Harry Hedgehog Cafe</li>
                  <li><strong>From Harajuku:</strong> Do the Kawaii Monster Cafe, then Chiku Chiku for hedgehogs</li>
                  <li><strong>Add an owl cafe</strong> in the same area (but not the same day—respect the animals)</li>
                </ul>

                <h2 className="text-3xl font-bold text-white mt-12 mb-4">Budget Breakdown</h2>
                <div className="bg-[#1A1A1A] rounded-xl p-6">
                  <ul className="space-y-3 text-gray-300 list-none pl-0">
                    <li className="flex justify-between">
                      <span>• Hedgehog Cafe Entry (1 hour)</span>
                      <strong className="text-[#FF006E]">¥1,500-2,000</strong>
                    </li>
                    <li className="flex justify-between">
                      <span>• Extra drinks/snacks</span>
                      <strong className="text-[#FF006E]">¥500-800</strong>
                    </li>
                    <li className="flex justify-between">
                      <span>• Transport to Roppongi/Harajuku</span>
                      <strong className="text-[#FF006E]">¥400</strong>
                    </li>
                    <li className="flex justify-between border-t border-gray-700 pt-3 mt-3">
                      <span className="font-bold">Total</span>
                      <strong className="text-[#00E5FF] text-lg">¥2,400-3,200</strong>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Bottom CTA */}
            <div className="mt-12 bg-gradient-to-r from-[#1A1A1A] to-[#0A0A0A] p-8 rounded-2xl border-2 border-[#FF006E]/30">
              <h3 className="text-2xl font-bold text-white mb-4">Ready for Hedgehog Cuteness?</h3>
              <p className="text-gray-300 mb-6">
                Book your tickets in advance—these cafes sell out fast, especially on weekends!
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <a href="https://www.klook.com/en-US/search/?query=tokyo+animal+cafe" target="_blank" rel="noopener noreferrer" className="px-8 py-4 bg-gradient-to-r from-[#FF006E] to-[#CC0058] text-white font-bold rounded-full hover:scale-105 transition-transform text-center">
                  Check Ticket Availability →
                </a>
                <a href="https://www.viator.com/Tokyo-tours/Animal-Cafes/d334-g24" target="_blank" rel="noopener noreferrer" className="px-8 py-4 bg-[#1A1A1A] border-2 border-[#FF006E] text-[#FF006E] font-bold rounded-full hover:bg-[#FF006E] hover:text-white transition-all text-center">
                  See All Animal Cafes →
                </a>
              </div>
            </div>
          </article>

          {/* Sidebar - Top 3 Picks */}
          <aside className="lg:w-1/3">
            <div className="sticky top-24">
              <div className="bg-gradient-to-br from-[#1A1A1A] to-[#0A0A0A] rounded-2xl p-6 border-2 border-[#FF006E]/30">
                <h3 className="text-2xl font-bold text-white mb-6 flex items-center gap-2">
                  <Star className="w-6 h-6 text-[#FF006E]" fill="#FF006E" />
                  Top 3 Picks
                </h3>
                
                <div className="space-y-4">
                  {topPicks.map((pick) => (
                    <a key={pick.id} href={pick.link} className="block bg-[#0A0A0A]/50 rounded-xl p-4 border border-[#FF006E]/20 hover:border-[#FF006E] transition-all">
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <div className="text-xs text-gray-500 uppercase font-bold mb-1">{pick.type}</div>
                          <h4 className="text-white font-semibold text-sm">{pick.name}</h4>
                        </div>
                        <div className="flex items-center gap-1 text-yellow-400 text-sm">
                          <Star className="w-3 h-3" fill="currentColor" />
                          {pick.rating}
                        </div>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-[#FF006E] font-bold text-sm">{pick.price}</span>
                        <span className="text-[#00E5FF] hover:text-white transition-colors text-xs font-semibold">
                          View Deal →
                        </span>
                      </div>
                    </a>
                  ))}
                </div>

                <div className="mt-6 pt-6 border-t border-gray-700">
                  <p className="text-gray-400 text-xs mb-3">
                    💰 Affiliate Disclosure: We earn from qualifying bookings at no extra cost to you.
                  </p>
                </div>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </div>
  )
}

export default HedgehogCafePage